<script setup>
// Mapa mental/diagrama PROFISSIONAL (v4 — exigência Ivan 26/08):
// - ARRASTAR livre (VueFlow, mesma engine do fluxo de campanhas);
// - criar/conectar pelos 4 LADOS (botão + em cima/baixo/esq/dir; alças nos
//   4 lados, modo loose = qualquer alça conecta em qualquer alça);
// - FORMATO do nó selecionável (balão, cartão, nota, texto) + cor por nó;
// - estilo das linhas (curva/reta/cotovelo, sólida/tracejada);
// - Tab filho à direita · Enter irmão · Delete exclui · 2 cliques edita.
import {
  ref,
  computed,
  watch,
  onMounted,
  onBeforeUnmount,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { VueFlow, useVueFlow, Handle } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { Controls } from '@vue-flow/controls';
import { MiniMap } from '@vue-flow/minimap';
import '@vue-flow/core/dist/style.css';
import '@vue-flow/core/dist/theme-default.css';
import '@vue-flow/controls/dist/style.css';
import '@vue-flow/minimap/dist/style.css';
import { useAlert } from 'dashboard/composables';
import { planningMapsAPI } from 'dashboard/api/workspace';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const COLORS = [
  '#2781f6',
  '#10b981',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#ec4899',
  '#14b8a6',
  '#f97316',
];

// Formatos de nó (pedido: "balão ou outro, tudo isso tem que poder escolher")
const SHAPES = [
  { key: 'balloon', icon: 'i-lucide-message-circle' },
  { key: 'card', icon: 'i-lucide-square' },
  { key: 'note', icon: 'i-lucide-sticky-note' },
  { key: 'text', icon: 'i-lucide-type' },
];

const SIDES = ['top', 'right', 'bottom', 'left'];
const OPPOSITE = { top: 'bottom', right: 'left', bottom: 'top', left: 'right' };

const mapId = route.params.mapId;
const mapName = ref('');
const nodes = ref([]);
const edges = ref([]);
const isLoading = ref(true);
const saveState = ref('saved');
const selectedId = ref(null);
const editingId = ref(null);

const {
  onConnect,
  addEdges,
  toObject,
  setViewport,
  screenToFlowCoordinate,
  findNode: vfNode,
} = useVueFlow();

const selectedNode = computed(() =>
  nodes.value.find(n => n.id === selectedId.value)
);

// ===== Linhas =====
const lineShape = ref('curve'); // curve | straight | elbow
const lineDashed = ref(false);
const LINE_SHAPES = [
  { key: 'curve', icon: 'i-lucide-spline' },
  { key: 'straight', icon: 'i-lucide-slash' },
  { key: 'elbow', icon: 'i-lucide-corner-down-right' },
];
const EDGE_TYPE = {
  curve: 'default',
  straight: 'straight',
  elbow: 'smoothstep',
};

function edgeProps(color) {
  return {
    type: EDGE_TYPE[lineShape.value],
    style: {
      stroke: color,
      strokeWidth: 2.5,
      strokeDasharray: lineDashed.value ? '7 6' : undefined,
    },
  };
}

// Trocar o estilo re-pinta TODAS as linhas existentes.
watch([lineShape, lineDashed], () => {
  edges.value = edges.value.map(edge => {
    const origem = nodes.value.find(n => n.id === edge.source);
    return { ...edge, ...edgeProps(origem?.data?.color || COLORS[0]) };
  });
});

onConnect(params => {
  const origem = nodes.value.find(n => n.id === params.source);
  addEdges([{ ...params, ...edgeProps(origem?.data?.color || COLORS[0]) }]);
});

// ===== Persistência =====
let saveTimer = null;
let suppressWatch = false;

async function persist() {
  saveState.value = 'saving';
  try {
    await planningMapsAPI.update(mapId, {
      name: mapName.value,
      data: {
        ...toObject(),
        line: { shape: lineShape.value, dashed: lineDashed.value },
      },
    });
    saveState.value = 'saved';
  } catch {
    saveState.value = 'dirty';
    useAlert(t('WORKSPACE_MAPS.SAVE_ERROR'));
  }
}

function scheduleSave() {
  if (suppressWatch) return;
  saveState.value = 'dirty';
  clearTimeout(saveTimer);
  saveTimer = setTimeout(persist, 1200);
}

watch([nodes, edges], scheduleSave, { deep: true });
watch(mapName, scheduleSave);
watch([lineShape, lineDashed], scheduleSave);

// ===== Criação =====
let seq = Date.now();
const newId = () => {
  seq += 1;
  return `n-${seq}`;
};

function startEdit(id) {
  selectedId.value = id;
  editingId.value = id;
  nextTick(() =>
    setTimeout(() => document.getElementById(`mapa-input-${id}`)?.focus(), 50)
  );
}

function makeNode({ position, color, shape, text = '', fontSize = 14 }) {
  return {
    id: newId(),
    type: 'idea',
    position,
    data: { label: text, color, shape, fontSize },
  };
}

// Tamanho da fonte por nó (pedido Ivan: aumentar/diminuir).
function bumpFontSize(id, delta) {
  const node = nodes.value.find(n => n.id === id);
  if (!node) return;
  const atual = node.data.fontSize || 14;
  node.data = {
    ...node.data,
    fontSize: Math.min(32, Math.max(10, atual + delta)),
  };
}

// Fixar nó (pedido Ivan: "opção de deixar fixo"): trava o arrasto.
function toggleLock(id) {
  const node = nodes.value.find(n => n.id === id);
  if (!node) return;
  const travado = node.data.locked !== true;
  node.draggable = !travado;
  node.data = { ...node.data, locked: travado };
}

function addFreeNode() {
  const position = screenToFlowCoordinate({
    x: window.innerWidth / 2,
    y: window.innerHeight / 2,
  });
  const node = makeNode({
    position,
    color: COLORS[nodes.value.length % COLORS.length],
    shape: 'balloon',
  });
  nodes.value = [...nodes.value, node];
  startEdit(node.id);
}

// Cria um filho NA DIREÇÃO pedida (4 lados), conectado da alça certa.
function addChildAt(parentId, side) {
  const pai = nodes.value.find(n => n.id === parentId);
  if (!pai) return;
  const medida = vfNode(parentId)?.dimensions || { width: 160, height: 44 };
  const irmaos = edges.value.filter(
    e => e.source === parentId && e.sourceHandle === side
  ).length;
  const desloca = irmaos * 90;
  const OFFSET = 190;
  const posicoes = {
    right: {
      x: pai.position.x + medida.width + OFFSET,
      y: pai.position.y + desloca,
    },
    left: {
      x: pai.position.x - medida.width - OFFSET,
      y: pai.position.y + desloca,
    },
    top: { x: pai.position.x + desloca, y: pai.position.y - 130 },
    bottom: {
      x: pai.position.x + desloca,
      y: pai.position.y + medida.height + 90,
    },
  };
  const filho = makeNode({
    position: posicoes[side],
    color: pai.data.color,
    shape: pai.data.shape || 'balloon',
    fontSize: pai.data.fontSize || 14,
  });
  nodes.value = [...nodes.value, filho];
  edges.value = [
    ...edges.value,
    {
      id: `e-${parentId}-${filho.id}`,
      source: parentId,
      target: filho.id,
      sourceHandle: side,
      targetHandle: OPPOSITE[side],
      ...edgeProps(pai.data.color),
    },
  ];
  startEdit(filho.id);
}

function addSibling(id) {
  const paiEdge = edges.value.find(e => e.target === id);
  if (paiEdge) addChildAt(paiEdge.source, paiEdge.sourceHandle || 'right');
  else addFreeNode();
}

function removeNode(id) {
  nodes.value = nodes.value.filter(n => n.id !== id);
  edges.value = edges.value.filter(e => e.source !== id && e.target !== id);
  selectedId.value = null;
  editingId.value = null;
}

function setNodeColor(id, color) {
  const node = nodes.value.find(n => n.id === id);
  if (!node) return;
  node.data = { ...node.data, color };
  edges.value = edges.value.map(e =>
    e.source === id ? { ...e, style: { ...e.style, stroke: color } } : e
  );
}

function setNodeShape(id, shape) {
  const node = nodes.value.find(n => n.id === id);
  if (node) node.data = { ...node.data, shape };
}

// ===== Teclado =====
function onKeydown(event) {
  if (editingId.value) {
    if (event.key === 'Escape') editingId.value = null;
    if (event.key === 'Tab') {
      event.preventDefault();
      const atual = editingId.value;
      editingId.value = null;
      addChildAt(atual, 'right');
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      const atual = editingId.value;
      editingId.value = null;
      addSibling(atual);
    }
    return;
  }
  if (!selectedId.value) return;
  if (event.key === 'Tab') {
    event.preventDefault();
    addChildAt(selectedId.value, 'right');
  } else if (event.key === 'Enter') {
    event.preventDefault();
    addSibling(selectedId.value);
  } else if (event.key === 'Delete' || event.key === 'Backspace') {
    event.preventDefault();
    removeNode(selectedId.value);
  } else if (event.key === 'F2') {
    startEdit(selectedId.value);
  }
}

// ===== Load (aceita v2 nodes/edges, v3 tree e o formato atual) =====
function treeToGraph(arvore) {
  const saida = { nodes: [], edges: [] };
  const anda = (no, x, y, cor) => {
    const corNo = no.color || cor || COLORS[0];
    saida.nodes.push({
      id: no.id,
      type: 'idea',
      position: { x, y },
      data: { label: no.text || '', color: corNo, shape: 'balloon' },
    });
    let cursor = y - ((no.children?.length || 1) - 1) * 60;
    (no.children || []).forEach(filho => {
      saida.edges.push({
        id: `e-${no.id}-${filho.id}`,
        source: no.id,
        target: filho.id,
        sourceHandle: 'right',
        targetHandle: 'left',
        type: 'default',
        style: { stroke: filho.color || corNo, strokeWidth: 2.5 },
      });
      anda(filho, x + 320, cursor, filho.color || corNo);
      cursor += 120;
    });
  };
  anda(arvore, 0, 0, arvore.color);
  return saida;
}

async function load() {
  try {
    const { data } = await planningMapsAPI.show(mapId);
    mapName.value = data.name;
    suppressWatch = true;
    if (data.data?.nodes?.length) {
      nodes.value = data.data.nodes.map(n => ({
        ...n,
        type: 'idea',
        data: {
          label: n.data?.label || '',
          color: n.data?.color?.startsWith('#') ? n.data.color : COLORS[0],
          shape: n.data?.shape || 'balloon',
        },
      }));
      edges.value = (data.data.edges || []).map(e => ({
        ...e,
        sourceHandle: e.sourceHandle || 'right',
        targetHandle: e.targetHandle || 'left',
      }));
    } else if (data.data?.tree) {
      const grafo = treeToGraph(data.data.tree);
      nodes.value = grafo.nodes;
      edges.value = grafo.edges;
    }
    lineShape.value = data.data?.line?.shape || 'curve';
    lineDashed.value = !!data.data?.line?.dashed;
    if (data.data?.viewport) setViewport(data.data.viewport);
    setTimeout(() => {
      suppressWatch = false;
    }, 300);
  } catch {
    useAlert(t('WORKSPACE_MAPS.LOAD_ERROR'));
    router.push({ name: 'planning_maps_index' });
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  load();
  window.addEventListener('keydown', onKeydown);
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown);
  clearTimeout(saveTimer);
  if (saveState.value === 'dirty') persist();
});
</script>

<template>
  <div class="flex flex-col flex-1 h-full bg-n-background overflow-hidden">
    <!-- Header -->
    <div
      class="flex items-center gap-3 px-4 py-3 border-b border-n-weak bg-n-background z-10"
    >
      <button
        class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
        @click="router.push({ name: 'planning_maps_index' })"
      >
        <span class="i-lucide-arrow-left w-4 h-4" />
      </button>
      <input
        v-model="mapName"
        class="flex-1 min-w-0 bg-transparent text-sm font-semibold text-n-slate-12 outline-none border-b border-transparent focus:border-n-brand"
        :placeholder="t('WORKSPACE_MAPS.NAME_PLACEHOLDER')"
      />
      <!-- Estilo das linhas -->
      <div
        class="flex items-center rounded-xl border border-n-weak bg-n-solid-2 p-0.5"
        :title="t('WORKSPACE_MAPS.LINE_STYLE')"
      >
        <button
          v-for="shape in LINE_SHAPES"
          :key="shape.key"
          class="p-1.5 rounded-lg transition-colors"
          :class="
            lineShape === shape.key
              ? 'bg-n-brand text-white'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          :title="t(`WORKSPACE_MAPS.LINE_${shape.key.toUpperCase()}`)"
          @click="lineShape = shape.key"
        >
          <span :class="shape.icon" class="w-4 h-4" />
        </button>
        <button
          class="p-1.5 rounded-lg transition-colors"
          :class="
            lineDashed
              ? 'bg-n-brand text-white'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          :title="t('WORKSPACE_MAPS.LINE_DASHED')"
          @click="lineDashed = !lineDashed"
        >
          <span class="i-lucide-tally-3 w-4 h-4" />
        </button>
      </div>
      <!-- Atalhos viram tooltip no "?" (o texto solto poluía o header) -->
      <button
        class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2 transition-colors"
        :title="t('WORKSPACE_MAPS.SHORTCUTS')"
      >
        <span class="i-lucide-circle-help w-4 h-4" />
      </button>
      <span class="text-xs text-n-slate-9">
        {{
          saveState === 'saved'
            ? t('WORKSPACE_MAPS.SAVED')
            : t('WORKSPACE_MAPS.SAVING')
        }}
      </span>
      <button
        class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-xl bg-n-brand text-white hover:bg-n-brand/90 transition-colors"
        @click="addFreeNode"
      >
        <span class="i-lucide-plus w-4 h-4" />
        {{ t('WORKSPACE_MAPS.ADD_IDEA') }}
      </button>
    </div>

    <!-- Barra do nó selecionado: FORMATO + cor + ações -->
    <div
      v-if="selectedNode"
      class="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-n-weak bg-n-solid-2 z-10"
    >
      <span class="text-xs text-n-slate-10">
        {{ t('WORKSPACE_MAPS.SHAPE') }}
      </span>
      <div
        class="flex items-center rounded-xl border border-n-weak bg-n-solid-1 p-0.5"
      >
        <button
          v-for="shape in SHAPES"
          :key="shape.key"
          class="p-1.5 rounded-lg transition-colors"
          :class="
            (selectedNode.data.shape || 'balloon') === shape.key
              ? 'bg-n-brand text-white'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          :title="t(`WORKSPACE_MAPS.SHAPE_${shape.key.toUpperCase()}`)"
          @click="setNodeShape(selectedNode.id, shape.key)"
        >
          <span :class="shape.icon" class="w-4 h-4" />
        </button>
      </div>
      <span class="ml-2 text-xs text-n-slate-10">
        {{ t('WORKSPACE_MAPS.BRANCH_COLOR') }}
      </span>
      <button
        v-for="color in COLORS"
        :key="color"
        class="w-4 h-4 rounded-full border-2 transition-transform hover:scale-110"
        :class="
          selectedNode.data.color === color
            ? 'border-n-slate-12'
            : 'border-transparent'
        "
        :style="{ backgroundColor: color }"
        @click="setNodeColor(selectedNode.id, color)"
      />
      <!-- Fonte A− / A+ -->
      <div
        class="ml-2 flex items-center gap-1 rounded-xl border border-n-weak bg-n-solid-1 p-0.5"
        :title="t('WORKSPACE_MAPS.FONT_SIZE')"
      >
        <button
          class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 transition-colors"
          @click="bumpFontSize(selectedNode.id, -2)"
        >
          <span class="i-lucide-a-arrow-down w-4 h-4" />
        </button>
        <span class="text-xs text-n-slate-11 tabular-nums w-5 text-center">
          {{ selectedNode.data.fontSize || 14 }}
        </span>
        <button
          class="p-1.5 rounded-lg text-n-slate-10 hover:text-n-slate-12 transition-colors"
          @click="bumpFontSize(selectedNode.id, 2)"
        >
          <span class="i-lucide-a-arrow-up w-4 h-4" />
        </button>
      </div>
      <!-- Fixar -->
      <button
        class="flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-xl border border-n-weak transition-colors"
        :class="
          selectedNode.data.locked
            ? 'bg-n-brand text-white border-transparent'
            : 'text-n-slate-11 hover:bg-n-alpha-2 bg-n-solid-1'
        "
        :title="t('WORKSPACE_MAPS.LOCK')"
        @click="toggleLock(selectedNode.id)"
      >
        <span
          :class="
            selectedNode.data.locked ? 'i-lucide-lock' : 'i-lucide-lock-open'
          "
          class="w-3.5 h-3.5"
        />
        {{
          selectedNode.data.locked
            ? t('WORKSPACE_MAPS.LOCKED')
            : t('WORKSPACE_MAPS.LOCK')
        }}
      </button>
      <div class="flex-1" />
      <button
        class="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-lg text-n-ruby-11 hover:bg-n-alpha-2 transition-colors"
        @click="removeNode(selectedNode.id)"
      >
        <span class="i-lucide-trash-2 w-3.5 h-3.5" />
        {{ t('WORKSPACE_MAPS.DELETE_BRANCH') }}
      </button>
    </div>

    <div v-if="isLoading" class="flex flex-1 items-center justify-center">
      <span
        class="i-lucide-loader-circle w-6 h-6 animate-spin text-n-slate-9"
      />
    </div>

    <VueFlow
      v-else
      v-model:nodes="nodes"
      v-model:edges="edges"
      class="flex-1"
      :min-zoom="0.2"
      :max-zoom="2.5"
      :delete-key-code="null"
      snap-to-grid
      :snap-grid="[12, 12]"
      connection-mode="loose"
      fit-view-on-init
      @pane-click="
        selectedId = null;
        editingId = null;
      "
    >
      <Background :gap="24" />
      <Controls position="bottom-left" />
      <MiniMap pannable zoomable position="bottom-right" />

      <div
        v-if="!nodes.length"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 text-n-slate-10 pointer-events-none"
      >
        <span class="i-lucide-git-fork w-10 h-10" />
        <p class="text-sm">{{ t('WORKSPACE_MAPS.CANVAS_EMPTY') }}</p>
      </div>

      <template #node-idea="{ id, data }">
        <div
          class="group/no relative"
          @click.stop="selectedId = id"
          @dblclick.stop="startEdit(id)"
        >
          <!-- Alças nos 4 lados (modo loose; somem durante a edição) -->
          <Handle
            v-for="side in SIDES"
            :id="side"
            :key="side"
            type="source"
            :position="side"
            class="mapa-alca"
            :class="{ 'mapa-alca-oculta': editingId === id }"
          />

          <!-- Nó nos formatos -->
          <div
            class="min-w-[130px] max-w-[280px] flex items-center justify-center px-4 py-2.5 cursor-pointer text-sm font-medium transition-shadow"
            :class="[
              (data.shape || 'balloon') === 'balloon'
                ? 'rounded-full border-2 text-n-slate-12 bg-n-solid-1'
                : '',
              data.shape === 'card' ? 'rounded-xl text-white shadow-md' : '',
              data.shape === 'note'
                ? 'rounded-md border text-slate-800 shadow-md'
                : '',
              data.shape === 'text' ? 'mapa-no-texto text-n-slate-12' : '',
            ]"
            :style="
              data.shape === 'card'
                ? { backgroundColor: data.color }
                : data.shape === 'note'
                  ? {
                      backgroundColor: `${data.color}33`,
                      borderColor: `${data.color}66`,
                      color: 'inherit',
                    }
                  : data.shape === 'text'
                    ? { borderBottom: `2.5px solid ${data.color}` }
                    : {
                        borderColor: data.color,
                        backgroundColor: `${data.color}1f`,
                      }
            "
            :data-selecionado="selectedId === id ? 'sim' : null"
          >
            <span
              v-if="data.locked"
              class="i-lucide-lock absolute -top-1.5 -left-1.5 w-3.5 h-3.5 text-n-slate-9"
            />
            <textarea
              v-if="editingId === id"
              :id="`mapa-input-${id}`"
              :value="data.label"
              rows="1"
              class="mapa-editor w-full text-center resize-none font-medium"
              :style="{ fontSize: `${data.fontSize || 14}px` }"
              :placeholder="t('WORKSPACE_MAPS.NOTE_PLACEHOLDER')"
              @input="data.label = $event.target.value"
              @blur="editingId = null"
              @click.stop
            />
            <p
              v-else
              class="text-center whitespace-pre-wrap break-words w-full"
              :class="data.shape === 'note' ? 'text-n-slate-12' : ''"
              :style="{ fontSize: `${data.fontSize || 14}px` }"
            >
              {{ data.label || t('WORKSPACE_MAPS.NOTE_PLACEHOLDER') }}
            </p>
          </div>

          <!-- Botões + nos 4 lados (somem durante a edição) -->
          <button
            v-for="side in SIDES"
            v-show="editingId !== id"
            :key="`mais-${side}`"
            class="mapa-mais absolute w-7 h-7 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover/no:opacity-100 transition-opacity z-20"
            :class="[
              side === 'right' ? '-right-3.5 top-1/2 -translate-y-1/2' : '',
              side === 'left' ? '-left-3.5 top-1/2 -translate-y-1/2' : '',
              side === 'top' ? '-top-3.5 left-1/2 -translate-x-1/2' : '',
              side === 'bottom' ? '-bottom-3.5 left-1/2 -translate-x-1/2' : '',
              selectedId === id ? 'opacity-100' : '',
            ]"
            :style="{ backgroundColor: data.color }"
            :title="t('WORKSPACE_MAPS.ADD_CHILD')"
            @click.stop="addChildAt(id, side)"
          >
            <!-- "+" desenhado à mão com TAMANHO EM ATRIBUTOS: a fonte de
                 ícones não renderiza no template de nó, e classes podem
                 perder pro CSS do VueFlow — assim é à prova de tudo. -->
            <svg
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="#ffffff"
              stroke-width="4"
              stroke-linecap="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </button>
        </div>
      </template>
    </VueFlow>
  </div>
</template>

<style scoped>
/* Alças discretas (o tema default do VueFlow pinta bolotas azuis — daí os
   !important): pontinho neutro que só aparece no hover. */
.mapa-alca {
  width: 8px !important;
  height: 8px !important;
  min-width: 8px !important;
  min-height: 8px !important;
  background: #64748b !important;
  border: 1px solid #cbd5e1 !important;
  opacity: 0;
  transition: opacity 0.15s;
}
.group\/no:hover .mapa-alca {
  opacity: 1;
}
.mapa-alca-oculta {
  opacity: 0 !important;
  pointer-events: none;
}
/* Editor herda a cor do nó e NÃO desenha caixa própria (o CSS global de
   textarea pintava um retângulo escuro com borda no meio do nó). */
.mapa-editor {
  background: transparent !important;
  color: inherit !important;
  box-shadow: none !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  outline: none !important;
  appearance: none !important;
  -webkit-appearance: none !important;
  border-radius: 0 !important;
  min-height: 1.4rem !important;
}
.mapa-editor::placeholder {
  color: currentColor;
  opacity: 0.5;
}
.mapa-no-texto {
  background: transparent;
}
/* Contorno de seleção. */
[data-selecionado='sim'] {
  box-shadow: 0 0 0 3px rgba(39, 129, 246, 0.4);
}
</style>
